import { useEffect, useState } from "react";
import { http, API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Truck, Download, Printer, ExternalLink, AlertCircle } from "lucide-react";

const TOKEN_KEY = "stc_token";

/**
 * Print Shipping Label section, embedded inside the Order modal.
 * - If a label exists already → shows tracking#, carrier, download/reprint button.
 * - Else: "Get Rates" → list rates → "Buy & Print"
 */
export default function OrderShippingActions({ order, onLabelPurchased, apiPrefix = "/admin/shipping/orders" }) {
  const [existing, setExisting] = useState(null);
  const [loadingRates, setLoadingRates] = useState(false);
  const [rates, setRates] = useState([]);
  const [error, setError] = useState(null);
  const [selectedRate, setSelectedRate] = useState(null);
  const [purchasing, setPurchasing] = useState(false);

  const loadExisting = async () => {
    try {
      const { data } = await http.get(`${apiPrefix}/${order.id}/label`);
      setExisting(data);
    } catch (e) {
      if (e?.response?.status !== 404) {
        // Silent for 404 (no label yet)
      }
      setExisting(null);
    }
  };

  useEffect(() => { loadExisting(); }, [order.id]);

  const fetchRates = async () => {
    setLoadingRates(true);
    setError(null);
    setRates([]);
    try {
      const { data } = await http.post(`${apiPrefix}/${order.id}/rates`);
      setRates(data.rates || []);
      if (!data.rates?.length) setError("No rates returned. Check carrier accounts in Shippo dashboard.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : (detail?.shippo_error?.detail || detail?.shippo_error || "Failed to load rates"));
    } finally {
      setLoadingRates(false);
    }
  };

  const buyLabel = async () => {
    if (!selectedRate) { toast.error("Pick a rate first"); return; }
    setPurchasing(true);
    try {
      await http.post(`${apiPrefix}/${order.id}/purchase-label`, { rate_id: selectedRate });
      toast.success("Label purchased!");
      await loadExisting();
      setRates([]); setSelectedRate(null);
      if (onLabelPurchased) onLabelPurchased();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.shippo_error || "Label purchase failed"));
    } finally {
      setPurchasing(false);
    }
  };

  const downloadPDF = () => {
    if (!existing?.id) return;
    // Open the proxied PDF download with bearer token in a new window
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${API_BASE}/api/admin/shipping/labels/${existing.id}/pdf`;
    // For Authorization-required downloads we need to fetch + blob
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => res.blob())
      .then((blob) => {
        const objUrl = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = objUrl;
        a.download = `label-${order.order_number}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
        setTimeout(() => URL.revokeObjectURL(objUrl), 1000);
      })
      .catch(() => toast.error("Could not download label"));
  };

  const hasAddress = !!(order.shipping_address && (order.shipping_address.line1 || order.shipping_address.street1));

  return (
    <div className="border border-white/10 bg-[#0a0a0a] p-4 space-y-3" data-testid="order-shipping-section">
      <div className="font-ui text-[10px] tracking-[0.32em] text-[#C9A055] flex items-center gap-2">
        <Truck size={12}/> SHIPPING LABEL
      </div>

      {existing ? (
        <div className="space-y-3" data-testid="order-shipping-label-exists">
          <div className="flex justify-between items-start gap-3 flex-wrap">
            <div className="text-sm">
              <div className="font-ui text-[10px] tracking-[0.22em] text-[#8a857d]">{(existing.carrier || "USPS").toUpperCase()} · TRACKING</div>
              <div className="font-mono text-[#F4F1EA] text-[13px] mt-1">{existing.tracking_number}</div>
              <div className="font-body text-[11px] text-[#8a857d] mt-1">Purchased {new Date(existing.created_at).toLocaleString()}{existing.amount ? ` · $${existing.amount}` : ""}</div>
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button onClick={downloadPDF} data-testid="order-label-download-btn" className="px-4 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
              <Download size={13}/> Download PDF
            </button>
            <button onClick={downloadPDF} className="px-4 py-2 border border-white/20 text-[#F4F1EA] hover:border-[#C9A055] font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
              <Printer size={13}/> Re-print
            </button>
            {existing.tracking_url_provider && (
              <a href={existing.tracking_url_provider} target="_blank" rel="noreferrer" className="px-4 py-2 border border-white/20 text-[#9a9690] hover:text-[#F4F1EA] hover:border-[#C9A055] font-ui text-[10px] tracking-[0.32em] uppercase inline-flex items-center gap-2">
                <ExternalLink size={13}/> Track
              </a>
            )}
          </div>
          <button onClick={() => { setExisting(null); fetchRates(); }} className="font-ui text-[10px] tracking-[0.32em] text-[#8a857d] hover:text-[#C9A055] uppercase">
            Re-purchase a new label →
          </button>
        </div>
      ) : !hasAddress ? (
        <div className="font-body text-[13px] text-orange-300 flex items-center gap-2"><AlertCircle size={14}/> No shipping address on this order — can't generate a label.</div>
      ) : rates.length === 0 ? (
        <div>
          <p className="font-body text-[12px] text-[#8a857d] mb-3">Get live rates from your default carrier and purchase a label in two clicks. The label PDF downloads instantly — print and stick.</p>
          <button onClick={fetchRates} disabled={loadingRates} data-testid="order-fetch-rates-btn" className="px-5 py-2 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40 inline-flex items-center gap-2">
            <Truck size={13}/> {loadingRates ? "Fetching rates…" : "Get Shipping Rates"}
          </button>
          {error && <div className="mt-3 font-body text-[12px] text-[#d97a7a]">{error}</div>}
        </div>
      ) : (
        <div className="space-y-3">
          <div className="font-body text-[12px] text-[#8a857d]">Choose a rate:</div>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {rates.map((rt) => (
              <label
                key={rt.object_id}
                data-testid={`rate-${rt.object_id}`}
                className={`flex items-center justify-between gap-3 p-3 border cursor-pointer ${selectedRate === rt.object_id ? "border-[#C9A055] bg-[#C9A055]/10" : "border-white/15 hover:border-white/30"}`}
              >
                <div className="flex items-center gap-3">
                  <input type="radio" checked={selectedRate === rt.object_id} onChange={() => setSelectedRate(rt.object_id)} className="accent-[#C9A055]" />
                  <div>
                    <div className="font-ui text-[12px] text-[#F4F1EA] uppercase">{rt.provider} {rt.servicelevel_name}</div>
                    <div className="font-body text-[11px] text-[#8a857d]">{rt.duration_terms || (rt.estimated_days ? `~${rt.estimated_days} days` : "")}</div>
                  </div>
                </div>
                <div className="font-ui-tight text-[15px] text-[#C9A055]">${rt.amount} {rt.currency}</div>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button onClick={buyLabel} disabled={!selectedRate || purchasing} data-testid="order-buy-label-btn" className="flex-1 px-5 py-2.5 bg-[#C9A055] text-black hover:opacity-90 font-ui text-[10px] tracking-[0.32em] uppercase disabled:opacity-40">
              {purchasing ? "Buying & generating…" : "Buy Label & Print"}
            </button>
            <button onClick={() => { setRates([]); setSelectedRate(null); }} className="px-4 py-2.5 border border-white/20 text-[#9a9690] font-ui text-[10px] tracking-[0.32em] uppercase">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
